import {
  useEffect,
  useRef,
  useState
} from 'react'
import {
  useDispatch,
  useSelector
} from 'react-redux'
import {
  Box,
  Stack,
  Text,
  useOutsideClick
} from '@chakra-ui/react'
import { AddSearchIcon } from 'assets/icons'
import { LoadingSpinier } from 'common/Loading'
import {
  getNotificationMessage,
  usersSelectors,
  markersSelectors
} from 'store'
import {
  FILTERS_TYPE,
  PENDING,
  REJECTED,
  SUCCEEDED
} from 'utils/constants'
import {
  isAuth,
  isNotMobile
} from 'utils/helpers'
import { COLORS } from 'utils/styles'
import { v4 as uuid } from 'uuid'
import { motion } from 'framer-motion'
import { StyledInput } from 'components/SearchContainer/SearchBarInput/styled'

const MotionStack = motion(Stack)

export const SearchBarInput = ({
  width = '100%',
  height = '100%',
  onKeyDown,
  onChange,
  value,
  affinities,
  codeWords,
  addFilters,
  name = 'search',
  setIsCodeWordSelect,
  isCodeWordSelect,
  recentSearchesList,
  setRecentSearchesList,
  maxFiltersAlready
}) => {
  const dispatch = useDispatch()
  const activeProfileRef = useRef(null)
  const styledInputRef = useRef(null)
  const affinitiesListRef = useRef(null)
  const inputWrapperRef = useRef(null)
  const inputRef = useRef(null)
  const [isPopupOpen, setIsPopupOpen] = useState(false)
  const [zIndexForDD, setzIndexForDD] = useState(401)
  const {
    activeProfile,
    filters,
    getUserProfileStatus,
    userProfile
  } = useSelector(usersSelectors)
  const {
    getMarkersWithCWStatus,
    markers
  } = useSelector(markersSelectors)
  const [affinityList, setAffinityList] = useState('')
  const [recentSearchList, setRecentSearchList] = useState([])
  const [hasMounted, setHasMounted] = useState(false)
  const [hideShadow, setHideShadow] = useState(false)
  const markersRef = useRef([])
  const userProfileRef = useRef([])

  const showPopup = (codeWords.length > 0 || affinities.length > 0)
    && isPopupOpen
    && !isCodeWordSelect

  useOutsideClick({
    ref: inputRef,
    handler: () => {
      setIsPopupOpen(false)
    }
  })

  const affinityListBtn = (item) => (
    <Stack
      cursor="pointer"
      key={uuid()}
      direction="row"
      spacing="10px"
      mr="5px !important"
      p="2px 14px"
      maxHeight="28px"
      borderRadius="5px"
      bg={COLORS.gold}
      width="max-content"
      alignItems="center"
      onClick={() => {
        if (
          !isAuth()
          || !activeProfileRef.current
        ) {
          dispatch(
            getNotificationMessage({
              type: REJECTED,
              message: 'You do not have an active profile. Please setup your profile first.'
            })
          )

          return
        }

        if (getMarkersWithCWStatus !== PENDING) {
          if (filters.length < 3) {
            const lowerValue = item.title.toLowerCase()

            let markerExisting = markersRef.current?.some(
              (marker) => marker?.affinities.some((a) => a?.toLowerCase() === lowerValue)
            )
            if (!markerExisting) {
              markerExisting = userProfileRef.current?.some(
                (up) => up?.affinities?.some((a) => a?.title?.toLowerCase() === lowerValue)
              )
            }

            addFilters({
              title: item.title,
              id: item.id,
              type: markerExisting ? FILTERS_TYPE.Affinities : FILTERS_TYPE.Own
            })

            if (recentSearchesList.includes(item.title)) {
              const filteredList = recentSearchesList.filter((i) => i !== item.title)
              filteredList.unshift(item.title)
              setRecentSearchesList(filteredList)
            } else if (recentSearchesList.length < 5) {
              setRecentSearchesList([item.title, ...recentSearchesList])
            } else if (recentSearchesList.length === 5) {
              const trimmed = recentSearchesList.slice(0, 4)
              setRecentSearchesList([item.title, ...trimmed])
            }

            setTimeout(() => {
              const ZoomCont = document.querySelector('.leaflet-control-zoom')
              const filterData = document.querySelector('.filter-data')
              const markerCountBox = document.querySelector('.marker-count')
              const markerPopupCont = document.querySelector('.leaflet-popup')
              ZoomCont.style.opacity = 1
              ZoomCont.style.pointerEvents = 'auto'
              filterData.style.opacity = 1
              filterData.style.pointerEvents = 'auto'
              if (markerPopupCont) {
                markerCountBox.style.opacity = 0
                markerCountBox.style.pointerEvents = 'none'
              } else {
                markerCountBox.style.opacity = 1
                markerCountBox.style.pointerEvents = 'auto'
              }
            }, 1000)
          } else if (filters.length >= 3) {
            maxFiltersAlready()
          }
        }
      }}
    >
      <Text cursor="pointer">{item.title}</Text>
    </Stack>
  )

  const getAffinityList = (searchValue = '') => {
    let tempAffinities = affinities

    if (searchValue !== '') {
      const tempSearchValue = searchValue.toLowerCase()

      tempAffinities = tempAffinities.filter((item) => {
        const title = item.title.toLowerCase()

        return tempSearchValue === title.substring(0, tempSearchValue.length)
      })
    }

    setAffinityList(tempAffinities.map((item) => affinityListBtn(item)))
  }

  const getRecentSearches = () => {
    const storedRecent = JSON.parse(localStorage.getItem('recentSearches')) || []
    const recentSearch = Array.isArray(storedRecent) ? storedRecent : Object.values(storedRecent)
    const recentTitles = recentSearch.map((title) => title.toLowerCase())

    const matchedAffinities = recentTitles
      .map((title) => affinities.find((item) => item.title.toLowerCase() === title))
      .filter(Boolean)

    setRecentSearchList(matchedAffinities.map((item) => affinityListBtn(item)))
  }

  const handleCodeWordSelect = (e) => {
    if (e === false) {
      setIsCodeWordSelect(true)
    } else if (e === true) {
      setIsCodeWordSelect(false)
    }
  }

  useEffect(() => {
    const handleOutsideClick = (e) => {
      styledInputRef.current?.blur()
      if (
        affinitiesListRef.current
        && !affinitiesListRef.current.contains(e.target)
        && !inputWrapperRef.current.contains(e.target)
      ) {
        setIsPopupOpen(false)
      }
    }
    // https://developer.mozilla.org/en-US/docs/Web/API/EventTarget/addEventListener#once
    // If {once: true}, the listener would be automatically removed when invoked.
    document.body.addEventListener('mousedown', handleOutsideClick, { once: true })
  }, [isPopupOpen])

  useEffect(() => {
    activeProfileRef.current = activeProfile
  }, [activeProfile])

  useEffect(() => {
    getAffinityList()
    getRecentSearches()
  }, [affinities, filters])

  useEffect(() => {
    onChange('')
  }, [filters])

  useEffect(() => {
    markersRef.current = markers ?? []
  }, [markers])

  useEffect(() => {
    userProfileRef.current = userProfile ?? []
  }, [userProfile])

  useEffect(() => {
    setHasMounted(true)
  }, [])

  return (
    <Stack
      ref={inputRef}
      position="relative"
      direction="column"
    >
      <Stack
        ref={inputWrapperRef}
        spacing={0}
        direction="row"
        zIndex={isAuth() ? 1408 : 1389}
      >
        <div className="code-word-toggle-container">
          <label style={{ cursor: 'pointer' }} htmlFor="code-word-toggle-input">
            <input type="checkbox" id="code-word-toggle-input" onChange={() => handleCodeWordSelect(isCodeWordSelect)} />
            <div className="toggle-for-code-word">
              <span>#</span>
            </div>
          </label>
        </div>
        <StyledInput
          ref={styledInputRef}
          type="text"
          maxLength="30"
          onFocus={() => {
            getRecentSearches()
            setIsPopupOpen(true)
          }}
          value={value}
          height={height}
          title="search-input"
          width={width}
          readOnly={getUserProfileStatus !== SUCCEEDED}
          autoComplete="off"
          onKeyDown={(e) => {
            if (e.target.value[0] === '#' && e.key !== 'Enter') {
              setIsPopupOpen(false)
            } else {
              setIsPopupOpen(true)
            }

            if (e.key === 'Enter') {
              getAffinityList('')
            }

            onKeyDown(e)
          }}
          onChange={(e) => {
            if (e.target.value[0] === '#') {
              setIsPopupOpen(false)
            } else {
              getAffinityList(e.target.value)
              getRecentSearches()
              setIsPopupOpen(true)
            }

            onChange(e.target.value)
          }}
          name={name}
        />

        <Box
          position="relative"
          display="flex"
          justifyContent="center"
          paddingRight="10px"
          alignItems="center"
          borderEndRadius="5px"
          background="white"
          cursor="pointer"
          onClick={() => {
            if (getMarkersWithCWStatus !== PENDING) {
              onKeyDown(new KeyboardEvent('keydown', {
                key: 'Enter',
                keyCode: 13,
                code: 'Enter',
                which: 13,
                bubbles: true,
                cancelable: true
              }))
            }
          }}
        >
          <Box
            position="absolute"
            display={getMarkersWithCWStatus === PENDING ? 'auto' : 'none'}
          >
            <LoadingSpinier width="15px" height="15px" />
          </Box>

          <Box
            visibility={getMarkersWithCWStatus === PENDING ? 'hidden' : 'visible'}
          >
            <AddSearchIcon />
          </Box>
        </Box>
      </Stack>

      <Stack
        position="absolute"
        overflow={hideShadow ? 'hidden' : 'visible'}
        width="100%"
        height="auto"
        top="41px"
        paddingBottom="10px"
      >
        {hasMounted && (
          <MotionStack
            width="100%"
            initial={{
              y: -500,
              height: '0px'
            }}
            animate={{
              y: showPopup ? 0 : -500,
              height: 'auto'
            }}
            transition={{
              type: 'spring',
              stiffness: 42,
              damping: 10
            }}
            onAnimationStart={() => {
              setHideShadow(true)
              if (showPopup) {
                setzIndexForDD(1421)
              }
            }}
            onAnimationComplete={() => {
              setHideShadow(false)
              if (!showPopup) {
                setzIndexForDD(401)
              }
            }}
            maxHeight="60svh"
            zIndex={zIndexForDD}
            overflow="hidden"
            borderRadius="10px"
            opacity={!showPopup && !hideShadow ? 0 : 1}
            boxShadow={hideShadow ? 'none' : '2xl'}
          >
            <Stack
              ref={affinitiesListRef}
              width="100%"
              maxHeight="500px"
              left="0px"
              borderRadius="10px"
              gap="0"
              bg="white"
              overflow="hidden"
              zIndex={401}
              borderBottomRadius="10px"
              height="auto"
            >
              <Stack
                p="15px"
                gap="10px"
                direction="row"
                width="100%"
                spacing={0}
                flexWrap="wrap"
                data-name="recentSearchResultList"
                borderBottom="1px solid"
                borderColor="blackAlpha.500"
                overflowY="scroll"
                sx={
                  isNotMobile()
                    ? {
                      '::-webkit-scrollbar': {
                        display: 'none'
                      },
                      '-ms-overflow-style': 'none',
                      'scrollbar-width': 'none'
                    } : {}
                }
              >
                <Text>
                  Recent
                </Text>
                {recentSearchesList ? recentSearchList : null}
              </Stack>

              <Stack
                p="15px"
                overflow="auto"
                gap="10px"
                direction="row"
                maxHeight="300px"
                width="100%"
                spacing={0}
                flexWrap="wrap"
                data-name="searchResultList"
                justifyContent={affinityList.length ? '' : 'center'}
              >
                {
                  affinityList.length
                    ? affinityList
                    : (<Text>No match found</Text>)
                }
              </Stack>
              <Text
                position="relative"
                background="white"
                width="100%"
                padding="2px"
                zIndex={99}
                borderBottomRadius="5px"
                textAlign="center"
                fontSize="14px"
                display={affinityList.length ? '' : 'none'}
              >
                Please select affinities from the list above
              </Text>
            </Stack>
          </MotionStack>
        )}
      </Stack>
    </Stack>
  )
}
